import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { Reveal, RevealGroup, RevealItem } from './Reveal';

export default function FAQ() {
  const { t } = useTranslation();
  const items = t('faq.items', { returnObjects: true }) as Array<{ q: string; a: string }>;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => {
    setOpenIndex(prev => (prev === i ? null : i));
  };

  // Split items into two columns for wider viewports
  const mid = Math.ceil(items.length / 2);
  const colA = items.slice(0, mid);
  const colB = items.slice(mid);

  return (
    <section id="faq" className="py-24 md:py-32 bg-[#f0eee6] dark:bg-[#1a1918]">
      <div className="mx-auto max-w-6xl px-6">
        {/* Header */}
        <Reveal className="mb-16 max-w-xl">
          <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-[#1414131a] dark:border-[#faf9f51a] px-4 py-1.5 text-[#6b6a68] dark:text-[#9a9893]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ae5630]" />
            FAQ
          </span>
          <h2 className="font-display display-tight mt-5 text-3xl font-medium leading-[1.1] text-[#141413] dark:text-[#faf9f5] md:text-5xl">
            {t('faq.title')}
          </h2>
        </Reveal>

        {/* Two-column split on desktop, single column on mobile */}
        <div className="grid gap-x-10 md:grid-cols-2">
          <FaqColumn items={colA} startIndex={0} openIndex={openIndex} onToggle={toggle} />
          <FaqColumn items={colB} startIndex={mid} openIndex={openIndex} onToggle={toggle} />
        </div>
      </div>
    </section>
  );
}

function FaqColumn({
  items,
  startIndex,
  openIndex,
  onToggle,
}: {
  items: Array<{ q: string; a: string }>;
  startIndex: number;
  openIndex: number | null;
  onToggle: (i: number) => void;
}) {
  return (
    <RevealGroup className="flex flex-col divide-y divide-[#1414131a] dark:divide-[#faf9f51a]">
      {items.map((item, localIdx) => {
        const i = startIndex + localIdx;
        const isOpen = openIndex === i;
        const panelId = `faq-panel-${i}`;
        const triggerId = `faq-trigger-${i}`;

        return (
          <RevealItem key={i}>
            <div className="py-1">
              <button
                id={triggerId}
                onClick={() => onToggle(i)}
                className="group flex w-full items-start justify-between gap-4 py-5 text-left transition-colors duration-300"
                aria-expanded={isOpen}
                aria-controls={panelId}
              >
                <span className="text-base font-medium text-[#141413] dark:text-[#faf9f5] leading-snug transition-colors duration-300 group-hover:text-[#ae5630]">
                  {item.q}
                </span>
                <span
                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[#1414131a] dark:border-[#faf9f51a] bg-[#faf9f5] dark:bg-[#141413] transition-all duration-300 ease-spring group-hover:border-[#ae5630] group-hover:text-[#ae5630] ${
                    isOpen ? 'rotate-45 border-[#ae5630] text-[#ae5630]' : 'text-[#87867f]'
                  }`}
                  aria-hidden="true"
                >
                  <Plus size={14} strokeWidth={2} />
                </span>
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    id={panelId}
                    role="region"
                    aria-labelledby={triggerId}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
                    className="overflow-hidden"
                  >
                    <p className="pb-5 text-sm leading-relaxed text-[#6b6a68] dark:text-[#9a9893] md:text-[15px]">
                      {item.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </RevealItem>
        );
      })}
    </RevealGroup>
  );
}
